import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const DEPLOY_COLORS = {
    live: "#4ecdc4",
    build_in_progress: "#f59e0b",
    update_in_progress: "#f59e0b",
    pre_deploy_in_progress: "#f59e0b",
    created: "#888",
    deactivated: "#555",
    canceled: "#555",
    build_failed: "#f87171",
    update_failed: "#f87171",
    pre_deploy_failed: "#f87171",
};

const SERVICE_TYPE_LABELS = {
    web_service: "Web",
    static_site: "Static",
    private_service: "Private",
    background_worker: "Worker",
    cron_job: "Cron",
};

function deployColor(status) {
    return DEPLOY_COLORS[status] || "#888";
}

function deployLabel(status) {
    if (!status) return "No deploys";
    return status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function serviceColor(suspended) {
    return suspended === "not_suspended" ? "#4ecdc4" : "#f87171";
}

function timeAgo(dateStr) {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatLogTime(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

const isHttpAccessLog = (msg) => /\bclientIP="/.test(msg || "");

const LogPanel = ({ serviceId, ownerId, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hideHttp, setHideHttp] = useState(true);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await authgear.refreshAccessTokenIfNeeded();
            const token = authgear.accessToken;
            const { data } = await axios.get(
                `${base_url}/render/services/${serviceId}/logs?ownerId=${ownerId}&limit=100`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // sort ascending by timestamp so newest logs are at the bottom
            const sorted = [...(data.logs || [])].sort(
                (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            setLogs(sorted);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [serviceId, ownerId]);

    const visibleLogs = hideHttp ? logs.filter(l => !isHttpAccessLog(l.message)) : logs;
    const hiddenCount = logs.length - visibleLogs.length;

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Auto-scroll to bottom when logs load
    const bottomRef = useCallback(node => {
        if (node) node.scrollIntoView({ behavior: "smooth" });
    }, []);

    return (
        <div style={{
            marginTop: "12px",
            background: "#0a0a14",
            border: "1px solid #2a2a4a",
            borderRadius: "8px",
            overflow: "hidden",
        }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px",
                borderBottom: "1px solid #1a1a2e",
                background: "#111120",
            }}>
                <span style={{ fontSize: "11px", color: "#666", fontFamily: "monospace" }}>
                    Logs (last 200 lines)
                    {hideHttp && hiddenCount > 0 && (
                        <span style={{ marginLeft: "8px", color: "#444" }}>
                            — {hiddenCount} HTTP request{hiddenCount === 1 ? "" : "s"} hidden
                        </span>
                    )}
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                    <button
                        onClick={() => setHideHttp(h => !h)}
                        style={{
                            padding: "2px 10px",
                            background: hideHttp ? "#1a2a3a" : "transparent",
                            border: "1px solid #2a2a4a",
                            borderRadius: "4px",
                            color: hideHttp ? "#00d4ff" : "#888",
                            cursor: "pointer",
                            fontSize: "11px",
                        }}
                    >
                        {hideHttp ? "Show HTTP" : "Hide HTTP"}
                    </button>
                    <button
                        onClick={fetchLogs}
                        disabled={loading}
                        style={{
                            padding: "2px 10px",
                            background: "transparent",
                            border: "1px solid #2a2a4a",
                            borderRadius: "4px",
                            color: loading ? "#444" : "#888",
                            cursor: loading ? "not-allowed" : "pointer",
                            fontSize: "11px",
                        }}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: "2px 10px",
                            background: "transparent",
                            border: "1px solid #2a2a4a",
                            borderRadius: "4px",
                            color: "#888",
                            cursor: "pointer",
                            fontSize: "11px",
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
            <div style={{
                height: "360px",
                overflowY: "auto",
                padding: "10px 14px",
                fontFamily: "monospace",
                fontSize: "12px",
                lineHeight: "1.6",
            }}>
                {error ? (
                    <span style={{ color: "#f87171" }}>Error: {error}</span>
                ) : loading && logs.length === 0 ? (
                    <span style={{ color: "#555" }}>Loading logs...</span>
                ) : logs.length === 0 ? (
                    <span style={{ color: "#555" }}>No logs found.</span>
                ) : visibleLogs.length === 0 ? (
                    <span style={{ color: "#555" }}>All {logs.length} log entries are HTTP requests (hidden).</span>
                ) : (
                    visibleLogs.map((entry, i) => (
                        <div key={i} style={{ display: "flex", gap: "12px", marginBottom: "2px" }}>
                            <span style={{ color: "#444", flexShrink: 0 }}>{formatLogTime(entry.timestamp)}</span>
                            <span style={{ color: entry.type === "stderr" ? "#f87171" : "#ccc", wordBreak: "break-all" }}>
                                {entry.message}
                            </span>
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>
        </div>
    );
};

const RenderDashboard = () => {
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [openLogs, setOpenLogs] = useState(null);

    const fetchServices = useCallback(async () => {
        try {
            await authgear.refreshAccessTokenIfNeeded();
            const token = authgear.accessToken;
            const { data } = await axios.get(`${base_url}/render/services`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const PINNED = ["peter lee gifford fe", "peter lee gifford server"];
            const sorted = [...data.services].sort((a, b) => {
                const aPin = PINNED.indexOf(a.name.toLowerCase());
                const bPin = PINNED.indexOf(b.name.toLowerCase());
                if (aPin !== -1 && bPin !== -1) return aPin - bPin;
                if (aPin !== -1) return -1;
                if (bPin !== -1) return 1;
                return 0;
            });
            setServices(sorted);
            setLastRefresh(new Date());
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchServices();

        const interval = setInterval(fetchServices, 10000);

        const es = new EventSource(`${base_url}/render/events`);
        es.onmessage = (e) => {
            if (e.data === "connected") return;
            fetchServices();
        };
        return () => {
            clearInterval(interval);
            es.close();
        };
    }, [navigate, fetchServices]);

    const card = {
        background: "#1a1a2e",
        border: "1px solid #2a2a4a",
        borderRadius: "10px",
        padding: "18px 20px",
    };

    const deployStatusDot = (status) => (
        <span style={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: deployColor(status),
            marginRight: "6px",
            flexShrink: 0,
        }} />
    );

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 20px", maxWidth: "900px", margin: "0 auto" }}>

                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <h2 style={{ color: "#00d4ff", margin: 0 }}>Render Services</h2>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {lastRefresh && (
                            <span style={{ fontSize: "11px", color: "#444" }}>
                                Updated {timeAgo(lastRefresh)}
                            </span>
                        )}
                        <button
                            onClick={fetchServices}
                            disabled={loading}
                            style={{
                                padding: "4px 12px",
                                background: "transparent",
                                border: "1px solid #2a2a4a",
                                borderRadius: "6px",
                                color: loading ? "#444" : "#888",
                                cursor: loading ? "not-allowed" : "pointer",
                                fontSize: "12px",
                            }}
                        >
                            {loading ? "Loading..." : "Refresh"}
                        </button>
                    </div>
                </div>

                {loading && services.length === 0 ? (
                    <p style={{ color: "#555", textAlign: "center", marginTop: "60px" }}>Loading...</p>
                ) : error ? (
                    <div style={{ background: "#2a1a1a", border: "1px solid #4a2a2a", borderRadius: "8px", padding: "16px", color: "#f88", fontSize: "13px" }}>
                        Error: {error}
                    </div>
                ) : services.length === 0 ? (
                    <div style={{ ...card, textAlign: "center", padding: "40px", color: "#555" }}>
                        No services found.
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {services.map(svc => {
                            const deploy = svc.latestDeploy;
                            const isLive = svc.suspended === "not_suspended";
                            const logsOpen = openLogs === svc.id;
                            return (
                                <div key={svc.id} style={card}>
                                    {/* Service header */}
                                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <div style={{
                                                width: "10px", height: "10px", borderRadius: "50%",
                                                background: serviceColor(svc.suspended), flexShrink: 0,
                                            }} />
                                            <div>
                                                <div style={{ fontWeight: "bold", fontSize: "14px", color: "#ddd" }}>
                                                    {svc.name}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>
                                                    {SERVICE_TYPE_LABELS[svc.type] || svc.type}
                                                    {!isLive && <span style={{ color: "#f87171", marginLeft: "6px" }}>Suspended</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                            {svc.url && (
                                                <a
                                                    href={svc.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ fontSize: "11px", color: "#00d4ff", wordBreak: "break-all" }}
                                                >
                                                    {svc.url.replace(/^https?:\/\//, "")}
                                                </a>
                                            )}
                                            <button
                                                onClick={() => setOpenLogs(logsOpen ? null : svc.id)}
                                                style={{
                                                    padding: "3px 10px",
                                                    background: logsOpen ? "#1a2a3a" : "transparent",
                                                    border: "1px solid #2a3a4a",
                                                    borderRadius: "4px",
                                                    color: logsOpen ? "#00d4ff" : "#667",
                                                    cursor: "pointer",
                                                    fontSize: "11px",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {logsOpen ? "Hide Logs" : "Logs"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Latest deploy */}
                                    <div style={{
                                        marginTop: "14px",
                                        paddingTop: "12px",
                                        borderTop: "1px solid #2a2a4a",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                    }}>
                                        <div style={{ display: "flex", alignItems: "center", fontSize: "12px" }}>
                                            {deployStatusDot(deploy?.status)}
                                            <span style={{ color: deployColor(deploy?.status), fontWeight: "bold" }}>
                                                {deployLabel(deploy?.status)}
                                            </span>
                                        </div>
                                        {deploy?.commit?.message && (
                                            <span style={{ fontSize: "12px", color: "#666", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                — {deploy.commit.message}
                                            </span>
                                        )}
                                        {deploy?.finishedAt && (
                                            <span style={{ fontSize: "11px", color: "#444", marginLeft: "auto", flexShrink: 0 }}>
                                                {timeAgo(deploy.finishedAt)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Log panel */}
                                    {logsOpen && (
                                        <LogPanel
                                            serviceId={svc.id}
                                            ownerId={svc.ownerId}
                                            onClose={() => setOpenLogs(null)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RenderDashboard;
